import type {ReactElement} from 'react';
import {useId, useState} from 'react';
import {resolveChatMediaUrl} from '@Libraries/chat-media-url.library';
import {xssCleanHtml} from '@Utils/string.util';
import {MediaItemSizeType} from '@Libraries/s3-library';
import {Text, TextSize} from '@Components/text/text';
import {Button, ButtonSize, ButtonType} from '@Components/button';
import {Pill} from '@Components/pill/pill';
import {PillSize} from '@Components/pill/pill.types';
import {Icon} from '@Components/icon-v2/icon';
import {IconSize} from '@Components/icon-v2/icon.types';
import {ENTITY_CARD_META_SEPARATOR, STATUS_TONE_PILL_TYPE} from '@Components/ai-chat-window/components/entity-card/entity-card.types';
import {MediaCountBadge} from '@Components/ai-chat-window/components/entity-card/components/media-count-badge/media-count-badge';
import type {EntityDraftCardProps, EntityDraftSection} from './entity-draft-card.types';
import {DESCRIPTION_TRUNCATE_LENGTH} from './entity-draft-card.types';
import {draftStatusOf} from './entity-draft-card.library';
import styles from './entity-draft-card.module.scss';

export function EntityDraftCard({title, meta, status, badge, description, mediaUrl, mediaCount, sections = [], notes = []}: EntityDraftCardProps): ReactElement {
  const [isExpanded, setIsExpanded] = useState(false);
  const descriptionId = useId();
  const draftStatus = draftStatusOf(status, badge);
  const isTruncatable = typeof description === 'string' && description.length > DESCRIPTION_TRUNCATE_LENGTH;

  const renderStatus = (): ReactElement | null => {
    if (draftStatus === undefined) {
      return null;
    }

    return <Pill label={draftStatus.label} type={STATUS_TONE_PILL_TYPE[draftStatus.tone]} size={PillSize.SM} />;
  };

  const renderTitle = (): ReactElement | null => {
    if (title === undefined || title === '') {
      return null;
    }

    return <Text val={title} size={TextSize.MD} bold className={styles.draftTitle} />;
  };

  const renderDescriptionToggle = (): ReactElement | null => {
    if (!isTruncatable) {
      return null;
    }

    return (
      <Button
        text={isExpanded ? window.i18next.t('pmwjs_show_less') : window.i18next.t('pmwjs_show_more')}
        type={ButtonType.TERTIARY}
        size={ButtonSize.SMALL}
        customClasses={styles.descriptionToggle}
        textClasses="_text-transform-none"
        attributes={{'aria-expanded': String(isExpanded), 'aria-controls': descriptionId}}
        onClick={(): void => {
          setIsExpanded((wasExpanded) => !wasExpanded);
        }}
      />
    );
  };

  const renderDescription = (): ReactElement | null => {
    if (description === undefined || description === '') {
      return null;
    }

    const shownText = isTruncatable && !isExpanded ? `${description.slice(0, DESCRIPTION_TRUNCATE_LENGTH).trimEnd()}…` : description;

    return (
      <div className={styles.draftDescription}>
        <Text val={shownText} size={TextSize.MD} className={styles.draftDescriptionText} attributes={{id: descriptionId}} />
        {renderDescriptionToggle()}
      </div>
    );
  };

  const renderSection = (section: EntityDraftSection, index: number): ReactElement => {
    return (
      <details key={`${section.label}-${index}`} className={styles.draftSection}>
        <summary className={styles.draftSectionSummary}>
          <Text val={section.label} size={TextSize.MD} bold className={styles.draftSectionLabel} />
          <Icon icon="icon-caret-down" size={IconSize.SIZE_ICON_16} className={styles.draftSectionChevron} ariaHidden />
        </summary>
        <Text val={section.text} size={TextSize.MD} className={styles.draftSectionText} />
      </details>
    );
  };

  const renderSections = (): ReactElement | null => {
    if (sections.length === 0) {
      return null;
    }

    return (
      <div className={styles.draftSectionList}>{sections.map(renderSection)}</div>
    );
  };

  const renderMedia = (): ReactElement | null => {
    const resolvedMediaUrl = resolveChatMediaUrl(mediaUrl, MediaItemSizeType.HIGH_RES);
    if (resolvedMediaUrl === null) {
      return null;
    }

    return (
      <div className={styles.draftMedia}>
        <img className={styles.draftMediaImage} src={resolvedMediaUrl} alt={title ?? meta[0] ?? ''} />
        <MediaCountBadge mediaCount={mediaCount} />
      </div>
    );
  };

  const renderNotes = (): ReactElement | null => {
    if (notes.length === 0) {
      return null;
    }

    return (
      <div className={styles.draftNotes}>
        {notes.map(
          (note, index): ReactElement => (
            <Text key={`${index}-${note}`} val={xssCleanHtml(note)} dangerouslySetInnerHTML size={TextSize.SM} className={styles.draftNote} />
          )
        )}
      </div>
    );
  };

  return (
    <div className={styles.entityDraftCard}>
      <div className={styles.draftHeader}>
        <Text val={meta.join(ENTITY_CARD_META_SEPARATOR)} size={TextSize.MD} bold className={styles.draftMeta} />
        {renderStatus()}
      </div>
      <hr className={styles.draftDivider} />
      {renderTitle()}
      {renderDescription()}
      {renderSections()}
      {renderMedia()}
      {renderNotes()}
    </div>
  );
}
